import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, addDays, startOfWeek, addWeeks, subWeeks } from 'date-fns';
import { 
  Calendar as CalendarIcon, Plus, Loader2, Clock, MapPin, User, Trash2, 
  ChevronLeft, ChevronRight, Users, UserPlus, UserMinus, GripVertical,
  MoreHorizontal, Copy, Filter, X, Edit2
} from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Calendar } from '../../components/ui/calendar';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Monday', short: 'Mon' },
  { value: 1, label: 'Tuesday', short: 'Tue' },
  { value: 2, label: 'Wednesday', short: 'Wed' },
  { value: 3, label: 'Thursday', short: 'Thu' },
  { value: 4, label: 'Friday', short: 'Fri' },
  { value: 5, label: 'Saturday', short: 'Sat' },
  { value: 6, label: 'Sunday', short: 'Sun' },
];

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  in_progress: 'bg-green-100 text-green-700 border-green-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
  missed: 'bg-red-100 text-red-700 border-red-200',
  unassigned: 'bg-amber-100 text-amber-700 border-amber-200',
};

// Shift Card Component
const ShiftCard = ({ shift, guards, onAssign, onUnassign, onDelete, onEdit }) => {
  const isUnassigned = !shift.guard_id;
  
  return (
    <div 
      className={`group relative p-3 rounded-lg border transition-all hover:shadow-md ${
        isUnassigned 
          ? 'bg-amber-50/50 border-amber-200 border-dashed' 
          : 'bg-white border-slate-200'
      }`}
      data-testid={`shift-card-${shift.id}`}
    >
      {/* Time Badge */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <Clock className="w-3 h-3" />
          {shift.start_time} - {shift.end_time}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(shift)}>
              <Edit2 className="w-4 h-4 mr-2" /> Edit Shift
            </DropdownMenuItem>
            {isUnassigned ? (
              <DropdownMenuItem onClick={() => onAssign(shift)}>
                <UserPlus className="w-4 h-4 mr-2" /> Assign Guard
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => onUnassign(shift.id)}>
                <UserMinus className="w-4 h-4 mr-2" /> Unassign Guard
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(shift.id)} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" /> Delete Shift
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Guard Info */}
      {isUnassigned ? (
        <button 
          onClick={() => onAssign(shift)}
          className="w-full flex items-center gap-2 p-2 rounded-md border border-dashed border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
            <UserPlus className="w-4 h-4 text-amber-700" />
          </div>
          <span className="text-sm font-medium text-amber-700">Assign Guard</span>
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <User className="w-4 h-4 text-blue-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{shift.guard_name}</p>
            <p className="text-xs text-slate-500 truncate flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {shift.site_name}
            </p>
          </div>
        </div>
      )}
      
      {/* Status Badge */}
      <div className="mt-2 flex items-center justify-between">
        <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[shift.status] || STATUS_COLORS.scheduled}`}>
          {shift.status}
        </Badge>
        {shift.site_name && !isUnassigned && (
          <span className="text-[10px] text-slate-400 truncate max-w-[80px]">{shift.site_name}</span>
        )}
      </div>
    </div>
  );
};

// Day Column Component
const DayColumn = ({ day, shifts, isToday, guards, onAssign, onUnassign, onDelete, onEdit, onAddShift }) => {
  return (
    <div className={`flex-1 min-w-[160px] ${isToday ? 'ring-2 ring-blue-500 ring-offset-2' : ''} rounded-xl`}>
      {/* Day Header */}
      <div className={`p-3 rounded-t-xl ${isToday ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>
        <div className="text-center">
          <p className={`text-xs font-medium ${isToday ? 'text-blue-100' : 'text-slate-500'}`}>
            {day.day_short}
          </p>
          <p className={`text-lg font-bold ${isToday ? 'text-white' : 'text-slate-800'}`}>
            {format(parseISO(day.date), 'd')}
          </p>
        </div>
      </div>
      
      {/* Shifts Container */}
      <div className={`p-2 min-h-[300px] rounded-b-xl border-x border-b ${isToday ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200 bg-slate-50/50'}`}>
        <ScrollArea className="h-[280px]">
          <div className="space-y-2 pr-2">
            {shifts.length === 0 ? (
              <button 
                onClick={() => onAddShift(day.date)}
                className="w-full p-4 rounded-lg border-2 border-dashed border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all group"
              >
                <Plus className="w-5 h-5 mx-auto text-slate-300 group-hover:text-blue-500" />
                <p className="text-xs text-slate-400 group-hover:text-blue-600 mt-1">Add shift</p>
              </button>
            ) : (
              <>
                {shifts.map((shift) => (
                  <ShiftCard 
                    key={shift.id} 
                    shift={shift} 
                    guards={guards}
                    onAssign={onAssign}
                    onUnassign={onUnassign}
                    onDelete={onDelete}
                    onEdit={onEdit}
                  />
                ))}
                <button 
                  onClick={() => onAddShift(day.date)}
                  className="w-full p-2 rounded-lg border border-dashed border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all opacity-50 hover:opacity-100"
                >
                  <Plus className="w-4 h-4 mx-auto text-slate-400" />
                </button>
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default function AdminSchedules() {
  const { toast } = useToast();
  const [weeklyData, setWeeklyData] = useState(null);
  const [guards, setGuards] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Modals
  const [showAddShift, setShowAddShift] = useState(false);
  const [showBulkShift, setShowBulkShift] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Edit shift form
  const [editForm, setEditForm] = useState({
    start_time: '',
    end_time: '',
    date: '',
    site_id: '',
    notes: '',
  });
  
  // Single shift form
  const [newShift, setNewShift] = useState({
    guard_id: '',
    site_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '08:00',
    end_time: '16:00',
  });

  // Bulk shift form
  const [bulkShift, setBulkShift] = useState({
    guard_ids: [],
    site_id: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    start_time: '08:00',
    end_time: '16:00',
    days_of_week: [0, 1, 2, 3, 4],
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const startStr = format(currentWeekStart, 'yyyy-MM-dd');
      const [weekData, guardsData, sitesData] = await Promise.all([
        api.getWeeklyShifts(startStr),
        api.getGuards(),
        api.getSites(),
      ]);
      setWeeklyData(weekData);
      setGuards(Array.isArray(guardsData) ? guardsData : []);
      setSites(Array.isArray(sitesData) ? sitesData : []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load schedule data",
      });
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart, toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const goToPreviousWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  const goToNextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  const goToToday = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const handleAddShift = async (e) => {
    e.preventDefault();
    if (!newShift.site_id) {
      toast({
        variant: "destructive",
        title: "Missing Fields",
        description: "Please select a site",
      });
      return;
    }

    setSubmitting(true);
    try {
      const shiftData = {
        ...newShift,
        guard_id: newShift.guard_id || null, // Allow null for unassigned
      };
      await api.createShift(shiftData);
      setShowAddShift(false);
      setNewShift({
        guard_id: '',
        site_id: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '08:00',
        end_time: '16:00',
      });
      toast({
        title: "Shift Created",
        description: newShift.guard_id ? "Shift scheduled and guard notified" : "Unassigned shift created",
      });
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to create shift',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkCreate = async (e) => {
    e.preventDefault();
    if (!bulkShift.site_id) {
      toast({
        variant: "destructive",
        title: "Missing Fields",
        description: "Please select a site",
      });
      return;
    }

    if (bulkShift.days_of_week.length === 0) {
      toast({
        variant: "destructive",
        title: "Missing Days",
        description: "Please select at least one day of the week",
      });
      return;
    }

    setSubmitting(true);
    try {
      // If no guards selected, create unassigned shifts
      const data = {
        ...bulkShift,
        guard_ids: bulkShift.guard_ids.length > 0 ? bulkShift.guard_ids : ['unassigned'],
      };
      
      const result = await api.createBulkShifts(data);
      setShowBulkShift(false);
      setBulkShift({
        guard_ids: [],
        site_id: '',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        end_date: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
        start_time: '08:00',
        end_time: '16:00',
        days_of_week: [0, 1, 2, 3, 4],
      });
      toast({
        title: "Shifts Created",
        description: `Successfully created ${result.shifts_created} shifts`,
      });
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to create bulk shifts',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignGuard = async (guardId) => {
    if (!selectedShift || !guardId) return;
    
    setSubmitting(true);
    try {
      await api.assignGuardToShift(selectedShift.id, guardId);
      setShowAssignModal(false);
      setSelectedShift(null);
      toast({
        title: "Guard Assigned",
        description: "Guard has been assigned and notified",
      });
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to assign guard',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassignGuard = async (shiftId) => {
    try {
      await api.unassignGuardFromShift(shiftId);
      toast({
        title: "Guard Unassigned",
        description: "Shift is now unassigned",
      });
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to unassign guard',
      });
    }
  };

  const handleDeleteShift = async (shiftId) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      await api.deleteShift(shiftId);
      toast({
        title: "Shift Deleted",
        description: "The shift has been removed",
      });
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || 'Failed to delete shift',
      });
    }
  };

  const openEditModal = (shift) => {
    setSelectedShift(shift);
    setEditForm({
      start_time: shift.start_time || '',
      end_time: shift.end_time || '',
      date: shift.date || '',
      site_id: shift.site_id || '',
      notes: shift.notes || '',
    });
    setShowEditModal(true);
  };

  const handleEditShift = async () => {
    if (!selectedShift) return;
    setSubmitting(true);
    try {
      const updateData = {};
      if (editForm.start_time !== selectedShift.start_time) updateData.start_time = editForm.start_time;
      if (editForm.end_time !== selectedShift.end_time) updateData.end_time = editForm.end_time;
      if (editForm.date !== selectedShift.date) updateData.date = editForm.date;
      if (editForm.site_id !== selectedShift.site_id) updateData.site_id = editForm.site_id;
      if (editForm.notes !== (selectedShift.notes || '')) updateData.notes = editForm.notes;

      if (Object.keys(updateData).length === 0) {
        toast({ title: "No changes", description: "No fields were modified" });
        setShowEditModal(false);
        return;
      }

      await api.updateShift(selectedShift.id, updateData);
      toast({ title: "Shift Updated", description: "The shift has been updated successfully" });
      setShowEditModal(false);
      loadData();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.message || 'Failed to update shift' });
    } finally {
      setSubmitting(false);
    }
  };


  const openAddShiftForDate = (date) => {
    setNewShift(prev => ({ ...prev, date }));
    setShowAddShift(true);
  };

  const openAssignModal = (shift) => {
    setSelectedShift(shift);
    setShowAssignModal(true);
  };

  const toggleGuardSelection = (guardId) => {
    setBulkShift(prev => ({
      ...prev,
      guard_ids: prev.guard_ids.includes(guardId)
        ? prev.guard_ids.filter(id => id !== guardId)
        : [...prev.guard_ids, guardId]
    }));
  };

  const toggleDaySelection = (day) => {
    setBulkShift(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort()
    }));
  };

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
            <p className="mt-3 text-slate-500">Loading schedule...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-schedules-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Shift Schedule</h1>
            <p className="text-slate-500 text-sm mt-1">
              Manage and assign shifts for your team
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowBulkShift(true)} 
              variant="outline" 
              className="gap-2"
              data-testid="bulk-create-btn"
            >
              <Copy className="w-4 h-4" /> Bulk Create
            </Button>
            <Button 
              onClick={() => {
                // Default to first day of current viewed week
                setNewShift(prev => ({ ...prev, date: format(currentWeekStart, 'yyyy-MM-dd') }));
                setShowAddShift(true);
              }} 
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="create-shift-btn"
            >
              <Plus className="w-4 h-4" /> New Shift
            </Button>
          </div>
        </div>

        {/* Week Navigation */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={goToPreviousWeek}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={goToNextWeek}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" onClick={goToToday} className="text-sm">
                  Today
                </Button>
              </div>
              
              <h2 className="text-lg font-semibold text-slate-800">
                {format(currentWeekStart, 'MMMM d')} - {format(addDays(currentWeekStart, 6), 'MMMM d, yyyy')}
              </h2>
              
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Assigned</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-amber-400 border border-dashed border-amber-600"></div>
                  <span>Unassigned</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Grid */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {weeklyData?.days?.map((day) => (
              <DayColumn
                key={day.date}
                day={day}
                shifts={day.shifts}
                isToday={day.date === todayStr}
                guards={guards}
                onAssign={openAssignModal}
                onUnassign={handleUnassignGuard}
                onDelete={handleDeleteShift}
                onEdit={openEditModal}
                onAddShift={openAddShiftForDate}
              />
            ))}
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CalendarIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {weeklyData?.days?.reduce((acc, day) => acc + day.shifts.length, 0) || 0}
                  </p>
                  <p className="text-xs text-slate-500">Total Shifts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <UserPlus className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">
                    {weeklyData?.days?.reduce((acc, day) => acc + day.shifts.filter(s => !s.guard_id).length, 0) || 0}
                  </p>
                  <p className="text-xs text-slate-500">Unassigned</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{guards.length}</p>
                  <p className="text-xs text-slate-500">Guards Available</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{sites.length}</p>
                  <p className="text-xs text-slate-500">Sites</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create Shift Sheet */}
      <Sheet open={showAddShift} onOpenChange={setShowAddShift}>
        <SheetContent className="sm:max-w-lg bg-white">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-slate-900">
              <Plus className="w-5 h-5 text-blue-600" />
              Create New Shift
            </SheetTitle>
            <SheetDescription className="text-slate-500">
              Create a shift and optionally assign it to a guard
            </SheetDescription>
          </SheetHeader>
          
          <form onSubmit={handleAddShift} className="space-y-6 mt-6">
            {/* Site Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Site *</Label>
              <Select 
                value={newShift.site_id} 
                onValueChange={(v) => setNewShift(prev => ({ ...prev, site_id: v }))}
              >
                <SelectTrigger className="h-11" data-testid="site-select">
                  <SelectValue placeholder="Select site location" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {site.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Date *</Label>
              <Input
                type="date"
                value={newShift.date}
                onChange={(e) => setNewShift(prev => ({ ...prev, date: e.target.value }))}
                className="h-11"
              />
            </div>

            {/* Time Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Start Time *</Label>
                <Input
                  type="time"
                  value={newShift.start_time}
                  onChange={(e) => setNewShift(prev => ({ ...prev, start_time: e.target.value }))}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">End Time *</Label>
                <Input
                  type="time"
                  value={newShift.end_time}
                  onChange={(e) => setNewShift(prev => ({ ...prev, end_time: e.target.value }))}
                  className="h-11"
                />
              </div>
            </div>

            {/* Guard Selection (Optional) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Assign Guard (Optional)</Label>
              <Select 
                value={newShift.guard_id || 'unassigned'} 
                onValueChange={(v) => setNewShift(prev => ({ ...prev, guard_id: v === 'unassigned' ? '' : v }))}
              >
                <SelectTrigger className="h-11" data-testid="guard-select">
                  <SelectValue placeholder="Leave empty for unassigned shift" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">
                    <div className="flex items-center gap-2 text-amber-600">
                      <UserPlus className="w-4 h-4" />
                      Leave Unassigned
                    </div>
                  </SelectItem>
                  {guards.map((guard) => (
                    <SelectItem key={guard.id} value={guard.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {guard.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                You can assign a guard later from the schedule
              </p>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAddShift(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="submit-shift-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Shift
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Bulk Create Sheet */}
      <Sheet open={showBulkShift} onOpenChange={setShowBulkShift}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-blue-600" />
              Bulk Create Shifts
            </SheetTitle>
            <SheetDescription>
              Create recurring shifts for multiple days
            </SheetDescription>
          </SheetHeader>
          
          <form onSubmit={handleBulkCreate} className="space-y-6 mt-6">
            {/* Site Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Site *</Label>
              <Select 
                value={bulkShift.site_id} 
                onValueChange={(v) => setBulkShift(prev => ({ ...prev, site_id: v }))}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select site location" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {site.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Start Date</Label>
                <Input
                  type="date"
                  value={bulkShift.start_date}
                  onChange={(e) => setBulkShift(prev => ({ ...prev, start_date: e.target.value }))}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">End Date</Label>
                <Input
                  type="date"
                  value={bulkShift.end_date}
                  onChange={(e) => setBulkShift(prev => ({ ...prev, end_date: e.target.value }))}
                  className="h-11"
                />
              </div>
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Start Time</Label>
                <Input
                  type="time"
                  value={bulkShift.start_time}
                  onChange={(e) => setBulkShift(prev => ({ ...prev, start_time: e.target.value }))}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">End Time</Label>
                <Input
                  type="time"
                  value={bulkShift.end_time}
                  onChange={(e) => setBulkShift(prev => ({ ...prev, end_time: e.target.value }))}
                  className="h-11"
                />
              </div>
            </div>

            {/* Days of Week */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Days of Week *</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDaySelection(day.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      bulkShift.days_of_week.includes(day.value)
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {day.short}
                  </button>
                ))}
              </div>
            </div>

            {/* Guards Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Assign Guards (Optional)</Label>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setBulkShift(prev => ({ ...prev, guard_ids: guards.map(g => g.id) }))}
                  >
                    All
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setBulkShift(prev => ({ ...prev, guard_ids: [] }))}
                  >
                    None
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-3 bg-slate-50 rounded-lg">
                {guards.map((guard) => (
                  <label 
                    key={guard.id} 
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      bulkShift.guard_ids.includes(guard.id) 
                        ? 'bg-blue-100 border border-blue-200' 
                        : 'bg-white border border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    <Checkbox
                      checked={bulkShift.guard_ids.includes(guard.id)}
                      onCheckedChange={() => toggleGuardSelection(guard.id)}
                    />
                    <span className="text-sm truncate">{guard.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {bulkShift.guard_ids.length === 0 
                  ? "No guards selected - shifts will be created as unassigned" 
                  : `${bulkShift.guard_ids.length} guard(s) selected`}
              </p>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
              <p className="text-sm font-medium text-blue-800 mb-1">Summary</p>
              <p className="text-sm text-blue-700">
                Creating shifts from <strong>{bulkShift.start_date}</strong> to{' '}
                <strong>{bulkShift.end_date}</strong> on{' '}
                <strong>{bulkShift.days_of_week.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.short).join(', ') || 'No days'}</strong>
                {bulkShift.guard_ids.length > 0 
                  ? ` for ${bulkShift.guard_ids.length} guard(s)`
                  : ' (unassigned)'}
              </p>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowBulkShift(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                Create Shifts
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Assign Guard Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Assign Guard
            </DialogTitle>
            <DialogDescription>
              {selectedShift && (
                <>
                  Assign a guard to the shift at <strong>{selectedShift.site_name}</strong> on{' '}
                  <strong>{selectedShift.date}</strong> ({selectedShift.start_time} - {selectedShift.end_time})
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4">
            {guards.map((guard) => (
              <button
                key={guard.id}
                onClick={() => handleAssignGuard(guard.id)}
                disabled={submitting}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{guard.name}</p>
                  <p className="text-xs text-slate-500">{guard.email}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Shift
            </DialogTitle>
            <DialogDescription>
              Update shift timing, date, or location
              {selectedShift?.guard_name && (
                <span className="block mt-1 text-slate-600 font-medium">
                  Guard: {selectedShift.guard_name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={editForm.start_time}
                  onChange={(e) => setEditForm(p => ({ ...p, start_time: e.target.value }))}
                  data-testid="edit-shift-start"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={editForm.end_time}
                  onChange={(e) => setEditForm(p => ({ ...p, end_time: e.target.value }))}
                  data-testid="edit-shift-end"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm(p => ({ ...p, date: e.target.value }))}
                data-testid="edit-shift-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Site</Label>
              <Select value={editForm.site_id} onValueChange={(v) => setEditForm(p => ({ ...p, site_id: v }))}>
                <SelectTrigger data-testid="edit-shift-site">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map(site => (
                    <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                placeholder="Add notes..."
                value={editForm.notes}
                onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))}
                data-testid="edit-shift-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEditShift} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
